<template>
  <div class="layout__search-results">
    <div class="layout__search-results-header">
      <template v-if="!isTyping && empty">
        <IconHistory />
        {{ t('general__text__search-recent') }}
      </template>
      <template v-else>
        {{
          isIndoorviewShowing
            ? `${t('general__text__search-results-for')} ${currentBuilding} (${totalResultsCount})`
            : `${t('general__text__search-results')} (${
                isLoading ? t('general__text__in-progress') : totalResultsCount
              })`
        }}
      </template>
    </div>
    <div v-if="!isTyping && empty" class="layout__search-recent">
      <div v-if="recentSearch.length === 0" class="layout__search-no-recent">
        {{ t('general__text__search-no-recent') }}
      </div>
      <ListStandard v-else :items="recentSearch" @select="onSelect" />
    </div>
    <template v-if="isTyping">
      <div v-if="mobileScreen" class="layout__search-results-container-mobile">
        <TabsUnderlineMenu
          :tabItems="data"
          :isLoading="isLoading"
          :searchString="searchString"
          @select="onSelect"
        />
        <ListItemLoading v-if="isLoading" :itemsToRender="10" />
      </div>
      <LayoutSearchState v-if="empty && !isLoading" :empty="true" :searchString="searchString" />
      <LayoutSearchState v-else-if="error && !isLoading && !empty" :error="true" />
      <div v-else-if="!mobileScreen" class="layout__search-results-container">
        <ListStandard
          v-for="(object, index) in data"
          :key="object + index"
          class="layout__search-results-list"
          :icon="object.icon"
          :title="object.title"
          :items="object.items"
          :isLoading="isLoading"
          :hasTooltips="true"
          @select="onSelect"
        >
          <ListItemLoading v-if="isLoading" :itemsToRender="9" />
        </ListStandard>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import ListStandard from '@/design-system/molecules/ListStandard.vue'
import IconHomeWork from '@/design-system/assets/icons/IconHomeWork.vue'
import IconRealEstate from '@/design-system/assets/icons/IconRealEstate.vue'
import IconHome from '@/design-system/assets/icons/IconHome.vue'
import IconMeetingRoom from '@/design-system/assets/icons/IconMeetingRoom.vue'
import IconHistory from '@/design-system/assets/icons/IconHistory.vue'
import IconLocationOn from '@/design-system/assets/icons/IconLocationOn.vue'
import TabsUnderlineMenu from '@/design-system/molecules/TabsUnderlineMenu.vue'
import LayoutSearchState from '@/design-system/molecules/LayoutSearchState.vue'
import { getRealmId } from '@/utils/realmId'
import { PropType, toRefs, ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import ListItemLoading from '@/design-system/atoms/ListItemLoading.vue'
import { ResultObject } from '@/interfaces/search.interface'

// props
const props = defineProps({
  searchResults: {
    type: Object,
    default: () => {}
  },
  isTyping: {
    type: Boolean,
    default: false
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  isIndoorviewShowing: {
    type: Boolean,
    default: false
  },
  recentSearch: {
    type: Array as PropType<Array<any>>,
    default: () => []
  },
  searchString: {
    type: String,
    default: ''
  },
  mobileScreen: {
    type: Boolean,
    default: false
  }
})
const { searchResults, isIndoorviewShowing, recentSearch, mobileScreen, isTyping } = toRefs(props)

// Emits (defineEmits)
const emits = defineEmits<{
  (e: 'selectedBuilding', value: ResultObject)
  (e: 'selectedComplex', value: ResultObject)
  (e: 'selectedRealEstate', value: ResultObject)
  (e: 'selectedRoom', value: ResultObject)
}>()

// reactive variables etc
const { t } = useI18n()
const store = useStore()
const currentBuilding = store.state.mapview.selectedBuilding.name
const realmId = getRealmId()
const error = ref<boolean>(false)
const empty = ref<boolean>(true)

// computed
const data = computed<Array<any>>(() => {
  const complexData = {
    title: realmId === 'akademiskahus' ? t('title__complex__campus') : t('general__title__complex'),
    icon: IconHomeWork,
    items: searchResults.value.complexes
  }
  const realEstateData = {
    title: t('general__data__realestates'),
    icon: IconRealEstate,
    items: searchResults.value.realEstates
  }
  const buildingData = {
    title: t('general__data__buildings'),
    icon: IconHome,
    items: searchResults.value.buildings
  }
  const roomData = {
    title: t('general__data__rooms'),
    icon: IconMeetingRoom,
    items: searchResults.value.rooms
  }
  const poiData = {
    title: t('general__text__pois-v2'),
    icon: IconLocationOn,
    items: searchResults.value.pois
  }
  return isIndoorviewShowing.value
    ? [poiData, roomData]
    : [complexData, realEstateData, buildingData, roomData]
})
const totalResultsCount = computed(() => {
  let dataCount = 0
  data.value.forEach((object) => {
    dataCount += object.items.length
  })
  return dataCount
})

// methods
function onSelect(item) {
  switch (item.type) {
    case 'COMPLEX':
      emits('selectedComplex', item)
      break
    case 'REAL_ESTATE':
      emits('selectedRealEstate', item)
      break
    case 'BUILDING':
      emits('selectedBuilding', item)
      break
    case 'ROOM':
      emits('selectedRoom', item)
      break
  }
}

watch(totalResultsCount, (newCount) => {
  if (newCount === 0) {
    empty.value = true
  } else empty.value = false
})
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';
.layout__search-results {
  column-gap: inherit;
  display: grid;
  grid-template-columns: inherit;
  grid-template-rows: auto auto 1fr;
  grid-column: 1 / -1;
  height: 100%;
  overflow: hidden;
  row-gap: variables.$padding-main-body;

  &-header {
    column-gap: variables.$gap-xlarge;
    display: flex;
    grid-row: 1;
    grid-column: 2 / 14;
    padding-top: variables.$padding-main-body;
    color: variables.$color-text-primary;
    @include font-mixins.desktop-bold-h5;

    @media screen and (max-width: 1024px) {
      @include font-mixins.mobile-bold-h5;
    }
  }
  &-container {
    column-gap: variables.$gap-xlarge;
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-column: 2 / 14;
    overflow: hidden;
    .list {
      grid-column: span 3;
      overflow: auto;
      width: 100%;
      &:not(:last-of-type) {
        border-right: variables.$border-width-xsmall solid variables.$color-disabled;
      }

      .list__item-loading-wrapper {
        padding-left: calc(2 * #{variables.$padding-xlarge});
      }
    }
    &-mobile {
      display: contents;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
      row-gap: variables.$padding-main-body;

      > :first-child {
        grid-column: 1 / -1;
      }
      > :nth-child(2),
      .list__item-loading-wrapper {
        grid-column: 2 / 14;
        margin-top: variables.$margin-xlarge;
      }
    }
  }
}
.layout__search-recent {
  grid-row: 2;
  grid-column: 2 / 14;
  padding-left: calc(#{variables.$padding-main-body} * 1.8);
}
.layout__search-no-recent {
  color: variables.$color-text-primary;
  @include font-mixins.desktop-regular-paragraph;

  @media screen and (max-width: 1024px) {
    @include font-mixins.mobile-regular-paragraph;
  }
}
</style>
